import React from 'react';
import PropTypes from 'prop-types';

import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { range } from 'lodash';

import Tooltip from 'components/tooltip';
import { formatDate } from 'helpers/formatters/date_time_formats';

export default class LeaseExpirationReminderForm extends React.Component {
  static propTypes = {
    dark: PropTypes.bool,
    disabled: PropTypes.bool,
    disabledTooltip: PropTypes.string,
    features: PropTypes.object,
    isAssignment: PropTypes.bool,
    readOnly: PropTypes.bool,
    renewalReminder: PropTypes.shape({
      dueDate: PropTypes.string,
      expirationCount: PropTypes.number,
      expirationUnit: PropTypes.string,
    }).isRequired,
    onChange: PropTypes.func.isRequired,
  }

  componentDidUpdate({ disabled }) {
    if (!disabled && this.props.disabled) {
      this.props.onChange({
        renewalReminder: {
          ...this.props.renewalReminder,
          expirationCount: null,
          expirationUnit: null
        }
      });
    }
  }

  handleSendExpirationReminderChange = (event) => {
    const { checked } = event.target;
    const { expirationCount, expirationUnit } = this.props.renewalReminder;

    this.props.onChange({
      renewalReminder: {
        ...this.props.renewalReminder,
        expirationCount: checked ? (expirationCount || 1) : null,
        expirationUnit: checked ? (expirationUnit || 'months') : null
      }
    });
  }

  handleExpirationSettingsChange = (event) => {
    const { name, value } = event.target;

    this.props.onChange({
      renewalReminder: {
        ...this.props.renewalReminder,
        [name]: name === 'expirationCount' ? parseInt(value) : value
      }
    });
  }

  render() {
    const { dark, disabled, disabledTooltip, readOnly } = this.props;

    return (
      <div>
        <div className="d-flex align-items-center gap-2">
          {((disabled || readOnly) && disabledTooltip) ? (
            <Tooltip content={disabledTooltip} dark={dark} placement="right">
              <div style={{ cursor: 'not-allowed' }}>{this.renderReminderCheckbox()}</div>
            </Tooltip>
          ) : this.renderReminderCheckbox()}

          <label>
            Get reminded when this lease is about to expire?
          </label>

          {this.renderInfoIcon()}
        </div>
        {this.renderExpirationReminderSettings()}
      </div>
    );
  }

  renderInfoIcon = () => {
    const { features = {}, isAssignment } = this.props;
    const typeLabel = isAssignment ? 'assignment' : 'listing';
    const tooltip = features.crm
      ? `Enabling this setting creates a new task with you as the assignee.
        On the specified date, an email reminder will be sent to the assignee,
        all users on the ${typeLabel}, and your office managers.`
      : `On the specified date, an email reminder will be sent to you,
        all users on the ${typeLabel}, and your office managers.`;

    return (
      <Tooltip
        content={tooltip}
        dark
        key="reminder-tooltip"
        placement="top"
      >
        <FontAwesomeIcon icon={faInfoCircle}/>
      </Tooltip>
    );
  }

  renderReminderCheckbox() {
    const { expirationCount, expirationUnit } = this.props.renewalReminder;
    const disabled = this.props.disabled || this.props.readOnly;

    return (
      <input
        checked={!!(expirationCount && expirationUnit)}
        disabled={disabled}
        name="sendExpirationReminder"
        style={{ pointerEvents: disabled ? 'none' : 'all' }}
        type="checkbox"
        onChange={this.handleSendExpirationReminderChange}
      />
    );
  }

  renderExpirationReminderSettings() {
    const { expirationCount, expirationUnit } = this.props.renewalReminder;
    const disabled = this.props.readOnly;

    if (expirationCount && expirationUnit) {
      return (
        <div className="d-flex align-items-center gap-2 mt-2">
          <span>Set a reminder</span>
          <select
            className="form-control w-auto"
            disabled={disabled}
            name="expirationCount"
            value={expirationCount}
            onChange={this.handleExpirationSettingsChange}
          >
            {range(1, 31).map(value => <option key={value} value={value}>{value}</option>)}
          </select>

          <select
            className="form-control w-auto"
            disabled={disabled}
            name="expirationUnit"
            value={expirationUnit}
            onChange={this.handleExpirationSettingsChange}
          >
            <option value="months">Months</option>
            <option value="years">Years</option>
          </select>

          <span>before the lease expires. ({
            formatDate(this.calculateReminderDate(expirationCount, expirationUnit))
          })</span>
        </div>
      );
    }
  }

  calculateReminderDate(count, units) {
    const parsedDate = moment(this.props.renewalReminder.dueDate);
    parsedDate.subtract(count, units);
    return parsedDate;
  }
}
